import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { emojiLibrary, emojiColorMap } from '../../lib/emojiLibrary';
import { EmojiOption } from '../../types';
import { motion } from 'framer-motion';

interface EmojiPickerProps {
  onSelect: (emoji: string, color: string) => void;
  partType: string;
}

export default function EmojiPicker({ onSelect, partType }: EmojiPickerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(partType);

  // Filter emojis based on search and category
  const filteredEmojis = emojiLibrary
    .filter(category => !selectedCategory || category.name.toLowerCase().includes(partType.toLowerCase()))
    .flatMap(category => category.emojis)
    .filter(emoji => 
      !searchTerm || 
      emoji.keywords.some(keyword => keyword.includes(searchTerm.toLowerCase())) ||
      emoji.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-midnight-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search emojis..."
          className="w-full pl-10 pr-4 py-2 border border-mint-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
        />
      </div>

      {/* Emoji Grid */}
      <div className="grid grid-cols-4 gap-2 max-h-[300px] overflow-y-auto p-2">
        {filteredEmojis.map((emoji, index) => (
          <motion.button
            key={`${emoji.emoji}-${index}`}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.1 }}
            onClick={() => onSelect(emoji.emoji, emoji.color)}
            className={`
              p-4 rounded-lg border-2 transition-all relative group
              ${emojiColorMap[emoji.color].bg}
              ${emojiColorMap[emoji.color].border}
              ${emojiColorMap[emoji.color].hover}
            `}
          >
            <span className="text-3xl block text-center">{emoji.emoji}</span>
            <div className="absolute inset-x-0 bottom-0 p-1 bg-white/90 text-xs text-center
              opacity-0 group-hover:opacity-100 transition-opacity rounded-b-lg
              truncate">
              {emoji.description}
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}